import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import localforage from 'localforage';

import type { SnackbarMessage } from '../types/book';
import { getDB } from './indexedDB';
import { getAppPlatform, saveBlobFileToAndroidDownloads, shareBlobFile } from './platform';

export type BackupProgressStage =
  | 'preparing-data'
  | 'collecting-books'
  | 'compressing'
  | 'saving'
  | 'finalizing';

export type BackupProgressState = {
  open: boolean;
  stage: BackupProgressStage;
  current: number;
  total: number;
  percent: number | null;
  detail: string;
  filename: string;
};

export const initialBackupProgressState: BackupProgressState = {
  open: false,
  stage: 'preparing-data',
  current: 0,
  total: 0,
  percent: null,
  detail: '',
  filename: '',
};

type TranslationFn = (key: string, variables?: Record<string, string | number>) => string;

export type CreateBackupZipOptions = {
  t: TranslationFn;
  setSnackbarMessage: (message: SnackbarMessage) => void;
  onProgress: (nextState: Partial<BackupProgressState>) => void;
};

const createBackupFilename = (): string => {
  const now = new Date();
  return `wereader_backup_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.zip`;
};

export const createBackupZip = async ({
  t,
  setSnackbarMessage,
  onProgress,
}: CreateBackupZipOptions): Promise<{ filename: string; succeeded: boolean }> => {
  const zip = new JSZip();
  const backupFilename = createBackupFilename();

  const updateBackupProgress = (nextState: Partial<BackupProgressState>) => {
    onProgress({
      ...nextState,
      open: true,
    });
  };

  console.debug('Opening backup progress dialog', {
    backupFilename,
    platform: getAppPlatform(),
  });
  updateBackupProgress({
    stage: 'preparing-data',
    current: 0,
    total: 0,
    percent: null,
    detail: '',
    filename: backupFilename,
  });

  try {
    const localForageKeys = await localforage.keys();
    updateBackupProgress({
      stage: 'preparing-data',
      current: 0,
      total: localForageKeys.length,
      percent: localForageKeys.length === 0 ? 100 : 0,
      detail: '',
    });

    const localForageData: { [key: string]: unknown } = {};
    for (let index = 0; index < localForageKeys.length; index += 1) {
      const key = localForageKeys[index];
      localForageData[key] = await localforage.getItem(key);
      updateBackupProgress({
        stage: 'preparing-data',
        current: index + 1,
        total: localForageKeys.length,
        percent: Math.round(((index + 1) / Math.max(localForageKeys.length, 1)) * 100),
        detail: key,
      });
    }
    console.debug('Preparing backup zip payload', {
      backupFilename,
      localForageKeyCount: Object.keys(localForageData).length,
    });
    zip.file(
      'localForageBackup.json',
      new Blob([JSON.stringify(localForageData)], {
        type: 'application/json',
      }),
    );

    const db = await getDB();
    const transaction = db.transaction(['ePubFiles'], 'readonly');
    const ePubFilesStore = transaction.objectStore('ePubFiles');
    const ePubFilesKeys = await ePubFilesStore.getAllKeys();

    updateBackupProgress({
      stage: 'collecting-books',
      current: 0,
      total: ePubFilesKeys.length,
      percent: ePubFilesKeys.length === 0 ? 100 : 0,
      detail: '',
    });
    console.debug('Collected IndexedDB entries for backup zip', {
      backupFilename,
      epubFileCount: ePubFilesKeys.length,
    });

    const metadataStore: { [key: string]: unknown } = {};
    for (let index = 0; index < ePubFilesKeys.length; index += 1) {
      const key = ePubFilesKeys[index];
      const keyString = String(key);
      const file = await ePubFilesStore.get(key);
      if (file) {
        zip.file(`ePubFiles/${keyString}`, file);

        if (!keyString.startsWith('cover-')) {
          metadataStore[keyString] = {
            name: file.name,
            type: file.type,
            lastModified: file.lastModified,
          };
        }
      }

      updateBackupProgress({
        stage: 'collecting-books',
        current: index + 1,
        total: ePubFilesKeys.length,
        percent: Math.round(((index + 1) / Math.max(ePubFilesKeys.length, 1)) * 100),
        detail: keyString,
      });
    }

    zip.file(
      'metadataBackup.json',
      new Blob([JSON.stringify(metadataStore)], {
        type: 'application/json',
      }),
    );

    updateBackupProgress({
      stage: 'compressing',
      current: 0,
      total: 100,
      percent: 0,
      detail: '',
    });
    let lastCompressionPercent = -1;
    let lastCompressionFile = '';
    const zipBlob = await zip.generateAsync({ type: 'blob' }, (metadata) => {
      const roundedPercent = Math.min(100, Math.max(0, Math.round(metadata.percent)));
      const currentFile = metadata.currentFile || '';
      if (roundedPercent === lastCompressionPercent && currentFile === lastCompressionFile) {
        return;
      }

      lastCompressionPercent = roundedPercent;
      lastCompressionFile = currentFile;
      updateBackupProgress({
        stage: 'compressing',
        current: roundedPercent,
        total: 100,
        percent: roundedPercent,
        detail: currentFile,
      });
    });
    console.debug('Generated backup zip blob', {
      backupFilename,
      size: zipBlob.size,
      type: zipBlob.type || 'application/octet-stream',
      platform: getAppPlatform(),
    });

    try {
      if (getAppPlatform() === 'android') {
        updateBackupProgress({
          stage: 'saving',
          current: 0,
          total: 0,
          percent: 0,
          detail: backupFilename,
        });
        await saveBlobFileToAndroidDownloads({
          blob: zipBlob,
          filename: backupFilename,
          onProgress: ({ chunkIndex, totalChunks, uploadedBytes, totalBytes }) => {
            updateBackupProgress({
              stage: 'saving',
              current: chunkIndex,
              total: totalChunks,
              percent: Math.round((uploadedBytes / Math.max(totalBytes, 1)) * 100),
              detail: `${uploadedBytes} / ${totalBytes} bytes`,
            });
          },
        });
        console.debug('Saved backup zip to Android Downloads', {
          backupFilename,
          size: zipBlob.size,
        });
        setSnackbarMessage({
          text: `Backup saved to Downloads as ${backupFilename}`,
          duration: 5000,
        });
        return { filename: backupFilename, succeeded: true };
      }

      updateBackupProgress({
        stage: 'finalizing',
        current: 0,
        total: 0,
        percent: null,
        detail: backupFilename,
      });
      const shared = await shareBlobFile({
        blob: zipBlob,
        filename: backupFilename,
        title: t('BACKUP_TITLE'),
        text: t('BACKUP_TEXT'),
      });

      if (!shared) {
        saveAs(zipBlob, backupFilename);
      }
      return { filename: backupFilename, succeeded: true };
    } catch (error) {
      console.error('Error exporting backup zip', {
        backupFilename,
        size: zipBlob.size,
        platform: getAppPlatform(),
        error,
      });
      saveAs(zipBlob, backupFilename);
      return { filename: backupFilename, succeeded: true };
    }
  } catch (error) {
    console.error('Failed to create backup zip', {
      backupFilename,
      platform: getAppPlatform(),
      error,
    });
    setSnackbarMessage({
      text: t('FAILED_TO_CREATE_BACKUP'),
      duration: 5000,
    });
    return { filename: backupFilename, succeeded: false };
  } finally {
    console.debug('Closing backup progress dialog', {
      backupFilename,
    });
  }
};
